/**
 * The client's parse boundary for a detached run's SSE channel. This is a
 * faithful mirror of the server `RunEvent` union (`apps/backend/src/agent/
 * run-events.ts`) — the frontend cannot depend on `@4f/backend` (a server
 * package), and the union is not in `@4f/shared`, so it is re-declared here as a
 * Zod schema and every frame off the wire is validated against it. Untrusted
 * bytes in, a typed {@link ChatEvent} out (or `null` for a corrupt frame — the
 * reader drops it and keeps tailing, never throws mid-stream).
 *
 * If the server union gains a member, add it here too; the `stream-model`
 * reducer switches exhaustively on `type`, so a missing case is a type error.
 */
import { z } from 'zod';

/** One event on a run's channel — the exact shape the edge writes as SSE `data`. */
export const ChatEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('run-started'),
    runId: z.string(),
    conversationId: z.string(),
    girlfriendId: z.string(),
  }),
  z.object({ type: z.literal('text-delta'), runId: z.string(), text: z.string() }),
  z.object({
    type: z.literal('tool-call'),
    runId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
  }),
  z.object({
    type: z.literal('tool-result'),
    runId: z.string(),
    toolName: z.string(),
    output: z.unknown(),
  }),
  z.object({ type: z.literal('run-finished'), runId: z.string(), messageId: z.string() }),
  /** The user stopped the run: terminal, but NOT a failure — nothing was billed. */
  z.object({ type: z.literal('run-canceled'), runId: z.string() }),
  /**
   * A failed run. `code` is the server's stable identifier (`content_blocked`,
   * `conversation_busy`, `upstream_error`, `internal_error`) and `message` is
   * already the client-safe projection — the server replaces any `>= 500` detail
   * before it ever reaches this frame, so nothing here needs sanitizing.
   *
   * `code` is OPTIONAL only for the rollout: a tab opened against the previous
   * deploy re-attaches to a run whose frames predate the field, and dropping those
   * frames as invalid would leave the composer locked on a run that is already over.
   */
  z.object({
    type: z.literal('run-error'),
    runId: z.string(),
    code: z.string().optional(),
    message: z.string(),
  }),
]);
export type ChatEvent = z.infer<typeof ChatEvent>;

/** A run's channel closes after exactly one of these terminal events. */
export function isTerminalEvent(event: ChatEvent): boolean {
  return (
    event.type === 'run-finished' || event.type === 'run-error' || event.type === 'run-canceled'
  );
}

/**
 * Parse one SSE `data` payload into a {@link ChatEvent}, or `null` when it is not
 * valid JSON or not a known event shape. Replay must survive a poisoned frame
 * (drop it, keep going) — this never throws.
 */
export function parseChatEvent(payload: string): ChatEvent | null {
  let value: unknown;
  try {
    value = JSON.parse(payload);
  } catch {
    return null;
  }
  const parsed = ChatEvent.safeParse(value);
  return parsed.success ? parsed.data : null;
}
